import { HttpErrorResponse } from '@angular/common/http'
import { Component, Inject, inject, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import {
  ApiService,
  AppointmentModel,
  ChargeFormService,
  ChargeRowFormGroup,
  ChargesFormArray,
  Clinic,
  ClinicService,
  CrudService,
  EpisodeModel,
  Estimate,
  EstimateCharge,
  EstimateModel,
  EstimateTemplateModel,
  FacilityService,
  getSelectedPhysicianIdFromAppointments,
  PatientDataService,
  PatientInsurance,
  ProductModel,
  seedChargeModifiers,
} from '@mmx/shared'
import { get, orderBy } from 'lodash'
import {
  BehaviorSubject,
  combineLatest,
  combineLatestWith,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs'
import { catchError, finalize } from 'rxjs/operators'

export interface ChargesDialogData {
  estimate?: EstimateModel
  facilityId?: string
  patientId: string
  appointmentId?: string
}

@Component({
  selector: 'mmx-charges-dialog',
  templateUrl: './charges-dialog.component.html',
  styleUrls: ['./charges-dialog.component.scss'],
  standalone: false,
})
export class ChargesDialogComponent implements OnInit, OnDestroy {
  private readonly chargeFormService = inject(ChargeFormService)

  clinic: Clinic
  estimate: EstimateModel
  errorMessage: string
  appointments: AppointmentModel[] = []
  episodes: EpisodeModel[] = []
  insurances: PatientInsurance[] = []
  template: EstimateTemplateModel
  estimateForm = this.fb.group({
    facilityId: this.fb.control<string>(null, Validators.required),
    templateId: this.fb.control<string>(null),
    saveTemplate: this.fb.control(false),
    name: this.fb.control<string>(null),
    episodeId: this.fb.control<string>(null),
    appointmentIds: this.fb.control<string[]>(null, Validators.required),
    patientType: this.fb.control<Estimate.PATIENT_TYPE>(Estimate.PATIENT_TYPE.OutPatient, Validators.required),
    charges: this.fb.array<FormGroup<ChargeRowFormGroup>>([], Validators.required),
  })
  filterTemplateCtrl = this.fb.control<string>(null)
  loading$: BehaviorSubject<boolean> = new BehaviorSubject(true)

  patientId$: Observable<string> = of(this.data.patientId)
  estimate$: Observable<EstimateModel> = of(this.data.estimate)
  templateId$: Observable<string> = of(this.data.estimate?.templateId)

  /** Getter for the charges FormArray */
  get chargesFormArray(): ChargesFormArray {
    return this.estimateForm.controls.charges
  }

  episodes$: Observable<EpisodeModel[]> = this.patientId$.pipe(
    filter(Boolean),
    switchMap(patientId => this.crudService.index(EpisodeModel, { patientId })),
    // map(resp => orderBy(resp.data, 'startAt', 'desc')),
    tap(episodes => this.episodes = episodes),
    shareReplay(1),
  )

  appointments$: Observable<AppointmentModel[]> = this.patientId$.pipe(
    filter(Boolean),
    switchMap(patientId => this.restService.getAppointments({ patientId, extend: 'products,estimates' })),
    map(resp => orderBy(resp.data, 'startAt', 'desc')),
    tap(appointments => {
      this.appointments = appointments

      if (this.data.appointmentId) {
        // if we were given an appointment, pre-select all appointments that share an Episode of Care
        const appointment = this.appointments.find(a => a.id === this.data.appointmentId)
        if (appointment?.episodeId) {
          const appointmentsInEpisode = this.appointments.filter(a => a.episodeId === appointment.episodeId)
          if (appointmentsInEpisode.length > 1) {
            this.estimateForm.patchValue({
              appointmentIds: appointmentsInEpisode.map(a => a.id),
            })
            this.estimateForm.updateValueAndValidity()
          }
        }
      }
    }),
    shareReplay(1),
  )

  private onDestroy$ = new Subject<void>()

  templates$ = combineLatest([
    this.filterTemplateCtrl.valueChanges.pipe(startWith(''), takeUntil(this.onDestroy$)),
    this.estimateForm.get('facilityId').valueChanges.pipe(startWith(this.data.estimate?.facilityId || this.data.facilityId), takeUntil(this.onDestroy$)),
  ]).pipe(
    takeUntil(this.onDestroy$),
    debounceTime(300),
    switchMap(([name, facilityId]) => this.restService.getEstimateTemplates({ name, facilityIds: facilityId ? [facilityId] : [] })),
  )

  facility$ = this.estimateForm.controls.facilityId.valueChanges.pipe(
    takeUntil(this.onDestroy$),
    startWith(this.estimateForm.value.facilityId),
    filter(Boolean),
    distinctUntilChanged(),
    combineLatestWith(this.facilitiesService.facilities$.pipe(takeUntil(this.onDestroy$))),
    map(([facilityId, facilities]) => facilities.find(f => f.id === facilityId)),
  )

  hbEnabled$ = this.clinicService.clinic$.pipe(
    takeUntil(this.onDestroy$),
    combineLatestWith(this.facility$.pipe(takeUntil(this.onDestroy$))),
    map(([clinic, facility]) => clinic.pricer.hb && (!facility || facility?.mmx?.hbFacilityId != null)),
  )

  private template$ = this.templateId$.pipe(
    switchMap(id => id ? this.crudService.read(EstimateTemplateModel, id) : of(null)),
    tap(template => {
      if (template) {
        this.template = template
        this.estimateForm.get('name').setValue(this.template.name)
      }
    }),
    catchError(() => {
      this.estimateForm.get('templateId').reset()
      this.estimate.templateId = null
      return of([])
    }),
    shareReplay(1),
  )

  private load$ = this.estimate$.pipe(
    tap(() => this.loading$.next(true)),
    combineLatestWith(this.appointments$, this.template$),
    tap(() => {
      if (this.estimate?.charges) {
        this.resetEstimateForm('estimate')
      } else if (this.estimate?.templateId) {
        this.resetEstimateForm('template')
      } else {
        this.resetEstimateForm()
      }
    }),
    tap(() => this.loading$.next(false)),
  )

  get submitDisabled(): boolean {
    return this.estimateForm.invalid || this.loading$.getValue()
  }

  get selectedPhysicianId(): string | null {
    return getSelectedPhysicianIdFromAppointments(
      this.appointments,
      this.estimateForm.controls.appointmentIds.value ?? [],
    )
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ChargesDialogData,
    private dialogRef: MatDialogRef<ChargesDialogComponent>,
    private clinicService: ClinicService,
    private restService: ApiService,
    private patientDataService: PatientDataService,
    private crudService: CrudService,
    private facilitiesService: FacilityService,
    private fb: FormBuilder,
  ) { }

  ngOnInit() {
    this.clinic = this.clinicService.clinic
    this.estimate = this.data.estimate

    if (this.estimate) {
      this.estimateForm.patchValue({
        facilityId: this.estimate.facilityId,
        templateId: this.estimate.templateId,
        appointmentIds: this.estimate.appointmentIds,
        patientType: this.estimate.patientType || Estimate.PATIENT_TYPE.OutPatient,
      })
      if (this.estimate.charges) {
        this.chargeFormService.setChargesOnFormArray(this.chargesFormArray, this.estimate.charges)
      }
      this.estimateForm.updateValueAndValidity()
    } else if (this.data.facilityId || this.data.appointmentId) {
      this.estimateForm.patchValue({
        facilityId: this.data.facilityId,
        appointmentIds: this.data.appointmentId ? [this.data.appointmentId] : [],
      })
      this.estimateForm.updateValueAndValidity()
    }

    this.load$.subscribe()

    this.estimateForm.controls.saveTemplate.valueChanges
      .subscribe((value) => {
        if (value) {
          this.estimateForm.controls.name.setValidators([Validators.required])
        } else {
          this.estimateForm.controls.name.clearValidators()
        }
        this.estimateForm.controls.name.updateValueAndValidity()
      })

    this.estimateForm.controls.facilityId.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        distinctUntilChanged(),
        tap(() => this.resetEstimateForm('clear')),
      )
      .subscribe()

    this.estimateForm.controls.templateId.valueChanges
      .pipe(
        filter(Boolean),
        tap(() => this.loading$.next(true)),
        switchMap(id => this.crudService.read(EstimateTemplateModel, id)),
      )
      .subscribe(template => {
        if (template) {
          this.template = template
          this.estimateForm.get('name').setValue(this.template.name)
          this.resetEstimateForm('template')
        }
        this.loading$.next(false)
      })

    this.estimateForm.controls.appointmentIds.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        distinctUntilChanged(),
        tap(() => this.resetEstimateForm()),
      )
      .subscribe()
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  addModelClaim(claim: any) {
    if (claim?.charges?.length) {
      this.resetEstimateToCharges(claim.charges)
    }
  }

  submitEstimate() {
    this.errorMessage = ''

    if (this.submitDisabled) {
      return
    }

    this.loading$.next(true)

    const estimateFormValue = this.estimateForm.value
    const charges = this.chargeFormService.getChargesFromFormArray(this.chargesFormArray)

    const template = new EstimateTemplateModel({
      id: estimateFormValue.templateId,
      name: estimateFormValue.name,
      facilityIds: [this.estimateForm.value.facilityId],
      charges,
    })

    if (!template.id) {
      delete template.id
    }

    // Observable for creating or updating the template
    const templateObservable = this.estimateForm.get('saveTemplate').value ?
      this.crudService.save(template) :
      of(null)

    templateObservable.pipe(
      switchMap((template) => {
        this.estimate = new EstimateModel({
          id: this.data.estimate?.id,
          patientId: this.data.patientId,
          facilityId: this.estimateForm.value.facilityId,
          templateId: template?.id ?? estimateFormValue.templateId,
          appointmentIds: estimateFormValue.appointmentIds,
          patientType: estimateFormValue.patientType,
          charges,
        })

        return this.crudService.save(this.estimate).pipe(
          map((estimate) => ({ estimate, template })),
        )
      }),
    ).subscribe({
      next: ({ estimate }) => {
        if (estimate) {
          this.dialogRef.close(estimate)
        }
      },
      error: (err: HttpErrorResponse) => {
        this.loading$.next(false)
        this.errorMessage = get(err, 'error.message', '') || get(err, 'error.error.message', '')
      },
    })
  }

  deleteEstimate() {
    this.loading$.next(true)
    this.crudService.delete(this.estimate).subscribe(
      () => {
        this.patientDataService.refreshDocuments$.next()
        this.loading$.next(false)
        this.dialogRef.close({ action: 'delete' })
      })
  }

  resetEstimateForm(type: string = 'appointment') {
    this.errorMessage = ''

    switch (type) {
      case 'template':
        this.estimateForm.get('facilityId').setValue(this.template.facilityIds ? this.template.facilityIds[0] : null)
        this.resetEstimateToCharges(this.template.charges)
        break
      case 'estimate':
        this.estimateForm.get('facilityId').setValue(this.estimate.facilityId)
        this.estimateForm.get('patientType').setValue(this.estimate.patientType)
        this.resetEstimateToCharges(this.estimate.charges)
        break
      case 'appointment':
        this.loading$.next(true)
        const appointmentIds: string[] = this.estimateForm.get('appointmentIds').value || []
        const cptCodes = appointmentIds.flatMap(id => {
          const appointment = this.appointments.find(app => app.id === id)
          this.estimateForm.get('facilityId').setValue(appointment.locationId)
          return appointment ? appointment.products.flatMap(product => product.getCPTCodes()) : []
        })

        if (cptCodes.length === 0) {
          this.loading$.next(false)
          return
        }

        const searchParams= {
          cptCodes,
          facilityIds: [this.estimateForm.get('facilityId').value],
        }

        this.restService.searchChargeDescriptionMaster(searchParams)
          .pipe(
            finalize(() => this.loading$.next(false)),
          )
          .subscribe({
            next: (charges) => {
              const data = charges.map(({ codeModifiers, ...charge }) => ({
                ...charge,
                description: charge.chargeDescription,
                modifiers: seedChargeModifiers(charge.modifiers, codeModifiers),
                quantity: 1,
              }))
              this.resetEstimateToCharges(data)
            },
            error: (err) => {
              this.errorMessage = get(err, 'error.error.message', '')
            },
          })
      case 'clear':
        this.estimateForm.get('templateId').reset()
        this.chargesFormArray.clear()
        this.chargesFormArray.updateValueAndValidity()
        break
    }
  }

  private resetEstimateToCharges(charges: EstimateCharge[]) {
    this.chargeFormService.setChargesOnFormArray(this.chargesFormArray, charges)
    this.chargesFormArray.updateValueAndValidity()
  }

  getProductsName(products: ProductModel[]) {
    return products.map((product) => product.name).join(', ')
  }
}
